import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { DrawingKind } from '@/lib/drawings/types'

import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { getDrawingSet } from '@/lib/drawings/buildings'
import { useInView } from '@/hooks/use-in-view'
import { PointCloud } from '@/components/generals/point-cloud'
import { DrawingSheet, SHEET_HEIGHT, SHEET_WIDTH } from '@/components/projects/drawing-sheet'

type Tab = DrawingKind | 'cloud'

const TABS: Array<{ id: Tab, label: string }> = [
    { id: 'plan', label: 'Plan' },
    { id: 'section', label: 'Section' },
    { id: 'elevation', label: 'Elevation' },
    { id: 'axonometric', label: 'Axonometric' },
    { id: 'cloud', label: 'Point cloud' }
]

interface DrawingSetProps {
    shape: string
    name: string
    inProgress: boolean
}

const DESCRIPTIONS: Record<DrawingKind, string> = {
    plan: 'Ground floor plan',
    section: 'Long section',
    elevation: 'Principal elevation',
    axonometric: 'Axonometric of the massing'
}

/**
 * Plan, section, elevation and axonometric drawn from fine lines that draw themselves in each time
 * a tab is chosen, plus the live point cloud. Arrow keys move between tabs.
 */
export const DrawingSet: FC<DrawingSetProps> = ({ shape, name, inProgress }): ReactNode => {
    const drawings = useMemo(() => getDrawingSet(shape.startsWith('generic') ? 'generic' : shape), [shape])
    const [tab, setTab] = useState<Tab>('plan')
    const [phase, setPhase] = useState<'pre' | 'in'>('pre')
    const [ref, inView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -20% 0px' })
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const baseId = `drawings-${shape.replace(/[^a-z0-9]/gi, '')}`

    useEffect(() => {
        if (!inView) return

        setPhase('pre')

        let second = 0
        const first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => setPhase('in'))
        })

        return () => {
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [tab, inView])

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

        event.preventDefault()

        const index = TABS.findIndex(item => item.id === tab)
        const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length

        setTab(TABS[next].id)
        tabRefs.current[next]?.focus()
    }

    return (
        <div ref={ref}>
            <div role='tablist' aria-label='Drawing set' onKeyDown={onKeyDown} className='flex flex-wrap border-b border-hairline px-gutter'>
                {TABS.map((item, index) => (
                    <button
                        key={item.id}
                        ref={element => {
                            tabRefs.current[index] = element
                        }}
                        type='button'
                        role='tab'
                        id={`${baseId}-tab-${item.id}`}
                        aria-selected={tab === item.id}
                        aria-controls={`${baseId}-panel`}
                        tabIndex={tab === item.id ? 0 : -1}
                        onClick={() => setTab(item.id)}
                        className={cn(
                            'hud relative -mb-px flex h-12 items-center gap-2 border-b px-3 transition-colors duration-300 first:pl-0',
                            tab === item.id ? 'border-signal text-ink' : 'border-transparent text-mute hover:text-ink'
                        )}
                    >
                        <span className={tab === item.id ? 'text-signal' : 'text-mute'}>
                            {String(index + 1).padStart(2, '0')}
                        </span>
                        {item.label}
                    </button>
                ))}
            </div>

            <div
                id={`${baseId}-panel`}
                role='tabpanel'
                aria-labelledby={`${baseId}-tab-${tab}`}
                className='relative px-gutter py-6'
            >
                {tab === 'cloud' ? (
                    <PointCloud
                        shape={shape}
                        label={`Point cloud of ${name}${inProgress ? ', a partial scan that grows as the build front advances' : ''}. Drag to orbit.`}
                        budget='preview'
                        rotate='always'
                        rotateSpeed={4}
                        hoverBoost={3}
                        interactive
                        effects
                        frame
                        grid
                        grow={inProgress}
                        className='aspect-[5/3] w-full border border-hairline'
                    />
                ) : (
                    <div className='relative border border-hairline'>
                        <svg
                            key={tab}
                            viewBox={`0 0 ${SHEET_WIDTH} ${SHEET_HEIGHT}`}
                            role='img'
                            aria-label={`${DESCRIPTIONS[tab]} of ${name}, drawn in fine lines with dimensions, grid lines and level marks.`}
                            data-reveal={phase}
                            className='block h-auto w-full'
                        >
                            <DrawingSheet drawing={drawings[tab]} />
                        </svg>

                        <p className='hud pointer-events-none absolute left-4 top-3 text-mute' aria-hidden='true'>
                            {DESCRIPTIONS[tab]} / {drawings[tab].scale}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
